import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import FunctionalButton from "components/layout/FunctionalButton";
import { useARC } from "providers/ARC/ARCProvider";
import { useToast } from "providers/ToastProvider";
import {
    ETHERNET_CONNECT_GREEN_BUTTON_CLASS,
    ETHERNET_CONNECT_GREEN_TITLE_CLASS,
    NETWORK_ROW_DISCONNECT_BUTTON_CLASS,
    NETWORK_ROW_DISCONNECT_TITLE_CLASS,
} from "lib/consts/networkSettingsActionButtons";
import {
    fetchMobileStatus,
    getMobileNetworkApiBaseUrl,
    MobileNetworkTimeoutError,
    postMobileApn,
    postMobilePower,
    type MobileStatusResponse,
} from "lib/mobileNetworkApi";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";
import {
    isNoMobileModemMessage,
    toUserFacingApiError,
} from "lib/utils/userFacingApiError";

const STATUS_INTERVAL_MS = 5000;

function isModemUnresponsiveMessage(message: string): boolean {
    const m = message.toLowerCase();
    return m.includes("timed out") || m.includes("timeout");
}

function toMobileUserFacingError(
    error: unknown,
    t: (key: string) => string,
    fallbackKey: string
): string {
    if (error instanceof MobileNetworkTimeoutError) {
        return t("mobileNetwork.modemUnresponsive");
    }
    const raw = error instanceof Error ? error.message : typeof error === "string" ? error : "";
    if (isNoMobileModemMessage(raw) || raw === "No mobile modem detected.") {
        return t("mobileNetwork.noModem");
    }
    return toUserFacingApiError(error, t(fallbackKey));
}

const ROW_GRID =
    "grid w-full max-w-2xl grid-cols-[minmax(0,7.5rem)_minmax(0,1fr)] gap-x-3 gap-y-0 border-b border-black80 py-2 text-h5 last:border-b-0";

function Row({ label, value }: { label: string; value: string }) {
    return (
        <div className={ROW_GRID} role="row">
            <div className="text-black40" role="rowheader">
                {label}
            </div>
            <div className="min-w-0 break-words text-white" role="cell">
                {value}
            </div>
        </div>
    );
}

function powerIsOff(powerState: string | null | undefined): boolean {
    if (!powerState) return false;
    const p = powerState.toLowerCase();
    return p.includes("off") || p.includes("low");
}

function modemIsDisabled(status: MobileStatusResponse | null): boolean {
    if (!status?.present) return true;
    if (powerIsOff(status.power_state)) return true;
    const st = (status.state ?? "").toLowerCase();
    return st === "disabled" || st === "disabling";
}

/** True when the host actually has usable mobile data (IP / bearer). */
function mobileDataIsOn(status: MobileStatusResponse | null): boolean {
    if (!status?.present || modemIsDisabled(status)) return false;
    if (status.data_path === "ecm") {
        return Boolean(status.ecm_ipv4?.trim());
    }
    return status.bearer_connected === true;
}

type DesiredMobileData = "on" | "off" | null;

/**
 * Connecting only while the user wants data On (or we inferred On) and it is not up yet.
 * Never "Connecting" after the user turns Mobile data Off.
 */
function mobileDataIsConnecting(
    status: MobileStatusResponse | null,
    desired: DesiredMobileData,
    powerBusy: boolean
): boolean {
    if (desired === "off") return false;
    if (modemIsDisabled(status) && desired !== "on") return false;
    if (mobileDataIsOn(status)) return false;
    if (desired === "on") return true;
    // No explicit desire yet: only show connecting during an in-flight enable
    return powerBusy;
}

function formatMobileDataState(
    t: (key: string) => string,
    status: MobileStatusResponse | null,
    desired: DesiredMobileData,
    powerBusy: boolean
): string {
    if (desired === "off" || (desired == null && modemIsDisabled(status))) {
        return t("mobileNetwork.mobileDataOff");
    }
    if (mobileDataIsOn(status)) return t("mobileNetwork.mobileDataOn");
    if (mobileDataIsConnecting(status, desired, powerBusy)) {
        return t("mobileNetwork.mobileDataConnecting");
    }
    return t("mobileNetwork.mobileDataOff");
}

const MobileNetworkSettingsPanel: React.FC = () => {
    const { t } = useTranslation(["settings", "common"]);
    const { addToast } = useToast();
    const { selectedARC } = useARC();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();
    const baseUrl = useMemo(
        () => getMobileNetworkApiBaseUrl(selectedARC?.host ?? null, isSecure, arcId),
        [selectedARC?.host, isSecure, arcId]
    );

    const [status, setStatus] = useState<MobileStatusResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const fetchInFlight = useRef(false);
    const [powerBusy, setPowerBusy] = useState(false);
    const [apnBusy, setApnBusy] = useState(false);
    const [apnInput, setApnInput] = useState("");
    const [userInput, setUserInput] = useState("");
    const [passwordInput, setPasswordInput] = useState("");
    const [reconnectAfterApn, setReconnectAfterApn] = useState(true);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    /** User has edited APN fields — stop overwriting with status autofill. */
    const apnFieldsTouched = useRef(false);
    /** User intent for Mobile data; drives Off/Connecting so we don't flash Connecting after Off. */
    const [desiredMobileData, setDesiredMobileData] = useState<DesiredMobileData>(null);

    const runFetch = useCallback(async () => {
        if (!baseUrl || fetchInFlight.current) return;
        fetchInFlight.current = true;
        setLoading(true);
        setError(null);
        try {
            const data = await fetchMobileStatus(baseUrl);
            setStatus(data);
            if (!apnFieldsTouched.current) {
                const suggested = data.apn?.trim() || data.apn_suggested?.trim() || "";
                if (suggested) setApnInput(suggested);
                const suggestedUser = data.apn_username?.trim() || "";
                if (suggestedUser) setUserInput(suggestedUser);
            }
            setDesiredMobileData((prev) => {
                if (prev === "off") {
                    // Keep Off until user turns On, even if modem status is laggy
                    return "off";
                }
                if (prev === "on") {
                    // Stay On-intent while restoring after boot; only clear if
                    // modem is down and persistence also says Off/unset.
                    if (
                        modemIsDisabled(data) &&
                        data.desired_mobile_data_enabled !== true
                    ) {
                        return "off";
                    }
                    return "on";
                }
                // First load: prefer persisted preference across Jetson reboot
                if (data.desired_mobile_data_enabled === true) return "on";
                if (data.desired_mobile_data_enabled === false) return "off";
                // No persisted preference yet: infer from live modem state
                if (mobileDataIsOn(data)) return "on";
                if (modemIsDisabled(data)) return "off";
                return null;
            });
        } catch (e) {
            setStatus(null);
            setError(toMobileUserFacingError(e, t, "mobileNetwork.loadFailed"));
        } finally {
            fetchInFlight.current = false;
            setLoading(false);
        }
    }, [baseUrl, t]);

    useEffect(() => {
        if (!baseUrl) {
            setError(t("mobileNetwork.hostNotSet"));
            setStatus(null);
            setDesiredMobileData(null);
            return;
        }
        setError(null);
        apnFieldsTouched.current = false;
        void runFetch();
        const id = window.setInterval(() => void runFetch(), STATUS_INTERVAL_MS);
        return () => window.clearInterval(id);
    }, [baseUrl, runFetch, t]);

    const dataOn = mobileDataIsOn(status);
    // Button: Turn off only while user wants data On (including Connecting). After Off → Turn on.
    const showAsOn = desiredMobileData === "on" || (desiredMobileData == null && dataOn);

    const handleMobileDataToggle = useCallback(async () => {
        if (!baseUrl || !status?.present || powerBusy) return;
        const targetEnabled = !showAsOn;
        setDesiredMobileData(targetEnabled ? "on" : "off");
        setPowerBusy(true);
        try {
            await postMobilePower(baseUrl, { enabled: targetEnabled, reconnect: true });
            addToast({
                type: "success",
                message: targetEnabled
                    ? t("mobileNetwork.mobileDataEnabled")
                    : t("mobileNetwork.mobileDataDisabled"),
            });
            void runFetch();
        } catch (e) {
            // Revert intent on failure
            setDesiredMobileData(targetEnabled ? "off" : "on");
            addToast({
                type: "error",
                message: toMobileUserFacingError(e, t, "mobileNetwork.powerCommandFailed"),
            });
        } finally {
            setPowerBusy(false);
        }
    }, [addToast, baseUrl, powerBusy, runFetch, showAsOn, status?.present, t]);

    const handleApplyApn = useCallback(async () => {
        if (!baseUrl || apnBusy) return;
        const apn = apnInput.trim();
        // Empty is allowed: backend applies Android-catalog suggestion from SIM MCC/MNC.
        setApnBusy(true);
        try {
            const r = await postMobileApn(baseUrl, {
                apn: apn || null,
                username: userInput.trim() || null,
                password: passwordInput || null,
                reconnect: reconnectAfterApn,
            });
            addToast({ type: "success", message: r.message || t("mobileNetwork.apnUpdated") });
            void runFetch();
        } catch (e) {
            addToast({
                type: "error",
                message: toMobileUserFacingError(e, t, "mobileNetwork.apnUpdateFailed"),
            });
        } finally {
            setApnBusy(false);
        }
    }, [addToast, apnBusy, apnInput, baseUrl, passwordInput, reconnectAfterApn, runFetch, userInput, t]);

    const hasApiError = status?.error != null && status.error !== "";
    const apiErrorRaw = hasApiError ? status?.error ?? null : null;
    const displayError =
        error ??
        (status && status.present === false
            ? t("mobileNetwork.noModem")
            : apiErrorRaw
              ? isModemUnresponsiveMessage(apiErrorRaw)
                  ? t("mobileNetwork.modemUnresponsive")
                  : isNoMobileModemMessage(apiErrorRaw)
                    ? t("mobileNetwork.noModem")
                    : toUserFacingApiError(apiErrorRaw, t("mobileNetwork.loadFailed"))
              : null);
    const canToggle = Boolean(baseUrl && status?.present && !displayError);
    const ipDisplay =
        desiredMobileData === "off" || modemIsDisabled(status)
            ? "—"
            : status?.ecm_ipv4?.trim() ||
              (status?.data_path === "ecm"
                  ? "—"
                  : status?.bearer_connected === true
                    ? t("mobileNetwork.bearerConnectedYes")
                    : "—");

    return (
        <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
            <div className="shrink-0 border-b border-black80 pb-4">
                <p className="text-h4 font-semibold text-white">{t("mobileNetwork.title")}</p>
                {baseUrl && !displayError && status?.present ? (
                    <p className="mt-1 text-h5 text-primary2">
                        {t("mobileNetwork.operator")}{" "}
                        <span className="font-semibold text-white">
                            {status.operator_name?.trim() || "—"}
                        </span>
                    </p>
                ) : null}

                {baseUrl && !displayError && status?.present ? (
                    <div className="mt-4 flex flex-wrap items-center gap-2">
                        <FunctionalButton
                            className={
                                showAsOn
                                    ? NETWORK_ROW_DISCONNECT_BUTTON_CLASS
                                    : ETHERNET_CONNECT_GREEN_BUTTON_CLASS
                            }
                            title={
                                powerBusy
                                    ? t("common:ellipsis")
                                    : showAsOn
                                      ? t("mobileNetwork.turnMobileDataOff")
                                      : t("mobileNetwork.turnMobileDataOn")
                            }
                            titleClassName={
                                showAsOn
                                    ? NETWORK_ROW_DISCONNECT_TITLE_CLASS
                                    : ETHERNET_CONNECT_GREEN_TITLE_CLASS
                            }
                            disabled={!canToggle || powerBusy}
                            onClick={() => void handleMobileDataToggle()}
                        />
                    </div>
                ) : null}
            </div>

            {displayError ? (
                <p className="mt-4 shrink-0 text-h5 text-red-400" role="alert">
                    {displayError}
                </p>
            ) : null}

            <div className="mt-4 min-w-0 shrink-0 rounded-r10 border border-black80 bg-black100 p-3">
                {!baseUrl ? null : loading && !status ? (
                    <div className="flex min-h-[8rem] flex-col items-center justify-center">
                        <p className="text-h5 text-black40">{t("common:loading")}</p>
                    </div>
                ) : status && !displayError ? (
                    <div className="w-full" role="table" aria-label={t("mobileNetwork.modemAriaLabel")}>
                        <Row
                            label={t("mobileNetwork.mobileData")}
                            value={formatMobileDataState(t, status, desiredMobileData, powerBusy)}
                        />
                        <Row label={t("mobileNetwork.operator")} value={status.operator_name?.trim() || "—"} />
                        <Row
                            label={t("mobileNetwork.signal")}
                            value={
                                desiredMobileData === "off" || modemIsDisabled(status)
                                    ? "—"
                                    : status.signal_quality_percent != null
                                      ? `${status.signal_quality_percent}%`
                                      : "—"
                            }
                        />
                        <Row label={t("mobileNetwork.ipAddress")} value={ipDisplay} />
                        {status.ecm_iface?.trim() && desiredMobileData !== "off" ? (
                            <Row label={t("mobileNetwork.interface")} value={status.ecm_iface.trim()} />
                        ) : null}
                    </div>
                ) : displayError ? null : (
                    <div className="flex min-h-[8rem] flex-col items-center justify-center">
                        <p className="text-h5 text-black60">{t("mobileNetwork.noStatusData")}</p>
                    </div>
                )}
            </div>

            {baseUrl && !displayError && status?.present ? (
                <div className="mt-4 shrink-0 border-t border-black80 pt-4">
                    <button
                        type="button"
                        className="flex w-full max-w-2xl items-center justify-between text-left text-h4 font-semibold text-white"
                        onClick={() => setAdvancedOpen((o) => !o)}
                        aria-expanded={advancedOpen}
                    >
                        <span>{t("mobileNetwork.advanced")}</span>
                        <span className="text-h5 text-black40">{advancedOpen ? "▾" : "▸"}</span>
                    </button>

                    {advancedOpen ? (
                        <div className="mt-3 space-y-4">
                            <div className="min-w-0 rounded-r10 border border-black80 bg-black100 p-3">
                                <div className="w-full" role="table" aria-label={t("mobileNetwork.advancedModemAria")}>
                                    <Row label={t("mobileNetwork.modem")} value={String(status.modem_index)} />
                                    <Row
                                        label={t("mobileNetwork.present")}
                                        value={status.present ? t("common:yes") : t("common:no")}
                                    />
                                    <Row
                                        label={t("mobileNetwork.manufacturer")}
                                        value={status.manufacturer?.trim() || "—"}
                                    />
                                    <Row label={t("mobileNetwork.model")} value={status.model?.trim() || "—"} />
                                    <Row label={t("mobileNetwork.state")} value={status.state?.trim() || "—"} />
                                    <Row label={t("mobileNetwork.power")} value={status.power_state?.trim() || "—"} />
                                    <Row label={t("mobileNetwork.access")} value={status.access_tech?.trim() || "—"} />
                                    <Row
                                        label={t("mobileNetwork.registration")}
                                        value={status.registration?.trim() || "—"}
                                    />
                                    <Row
                                        label={t("mobileNetwork.packet")}
                                        value={status.packet_service_state?.trim() || "—"}
                                    />
                                    <Row
                                        label={t("mobileNetwork.bearerConnected")}
                                        value={
                                            status.bearer_connected === true
                                                ? t("mobileNetwork.bearerConnectedYes")
                                                : status.bearer_connected === false
                                                  ? t("mobileNetwork.bearerConnectedNo")
                                                  : "—"
                                        }
                                    />
                                    <Row label={t("mobileNetwork.dataPath")} value={status.data_path?.trim() || "—"} />
                                    <Row
                                        label={t("mobileNetwork.internetConnectivity")}
                                        value={
                                            status.internet_connected === true
                                                ? t("mobileNetwork.internetConnected")
                                                : status.internet_connected === false
                                                  ? t("mobileNetwork.internetNotConnected")
                                                  : t("mobileNetwork.internetChecking")
                                        }
                                    />
                                    <Row label={t("mobileNetwork.imei")} value={status.imei?.trim() || "—"} />
                                    <Row
                                        label={t("mobileNetwork.apnEps")}
                                        value={
                                            status.apn?.trim()
                                                ? status.apn_source === "android_db"
                                                    ? `${status.apn.trim()}${
                                                          status.apn_carrier?.trim()
                                                              ? ` (${status.apn_carrier.trim()})`
                                                              : ""
                                                      } · ${t("mobileNetwork.apnFromCatalog")}`
                                                    : status.apn.trim()
                                                : "—"
                                        }
                                    />
                                </div>
                            </div>

                            <div className="space-y-3">
                                <p className="text-h4 font-semibold text-white">{t("mobileNetwork.apnTitle")}</p>
                                <p className="text-h5 text-black40">{t("mobileNetwork.apnDescription")}</p>
                                <div className="flex max-w-2xl flex-col gap-2">
                                    <label className="text-h5 text-black40" htmlFor="mobile-apn-input">
                                        {t("mobileNetwork.accessPointName")}
                                    </label>
                                    <input
                                        id="mobile-apn-input"
                                        className="rounded-r5 border border-black80 bg-black70 px-3 py-2 text-h5 text-white outline-none focus:border-primary2"
                                        value={apnInput}
                                        onChange={(e) => {
                                            apnFieldsTouched.current = true;
                                            setApnInput(e.target.value);
                                        }}
                                        placeholder={
                                            status.apn_suggested?.trim() ||
                                            status.apn?.trim() ||
                                            t("mobileNetwork.apnPlaceholder")
                                        }
                                        autoComplete="off"
                                    />
                                    <label className="mt-1 text-h5 text-black40" htmlFor="mobile-apn-user">
                                        {t("mobileNetwork.usernameOptional")}
                                    </label>
                                    <input
                                        id="mobile-apn-user"
                                        className="rounded-r5 border border-black80 bg-black70 px-3 py-2 text-h5 text-white outline-none focus:border-primary2"
                                        value={userInput}
                                        onChange={(e) => {
                                            apnFieldsTouched.current = true;
                                            setUserInput(e.target.value);
                                        }}
                                        autoComplete="off"
                                    />
                                    <label className="mt-1 text-h5 text-black40" htmlFor="mobile-apn-pass">
                                        {t("mobileNetwork.passwordOptional")}
                                    </label>
                                    <input
                                        id="mobile-apn-pass"
                                        type="password"
                                        className="rounded-r5 border border-black80 bg-black70 px-3 py-2 text-h5 text-white outline-none focus:border-primary2"
                                        value={passwordInput}
                                        onChange={(e) => {
                                            apnFieldsTouched.current = true;
                                            setPasswordInput(e.target.value);
                                        }}
                                        autoComplete="off"
                                    />
                                    <label className="mt-2 flex cursor-pointer items-center gap-2 text-h5 text-white">
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-black80 bg-black70 text-primary2"
                                            checked={reconnectAfterApn}
                                            onChange={(e) => setReconnectAfterApn(e.target.checked)}
                                        />
                                        {t("mobileNetwork.reconnectAfterApply")}
                                    </label>
                                    <div className="mt-2">
                                        <FunctionalButton
                                            className={ETHERNET_CONNECT_GREEN_BUTTON_CLASS}
                                            title={apnBusy ? t("common:ellipsis") : t("mobileNetwork.applyApn")}
                                            titleClassName={ETHERNET_CONNECT_GREEN_TITLE_CLASS}
                                            disabled={apnBusy}
                                            onClick={() => void handleApplyApn()}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

export default MobileNetworkSettingsPanel;
